import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { Field } from 'redux-form';

import { USER_ROLE } from 'constants/logic';
import { manualUserVenuesPropTypes } from 'constants/propTypes';
import Button from 'components/Button';
import Message from 'components/Message';
import Confirm from 'components/Confirm';
import When from 'components/When';
import { Input, InputLabel } from 'components/Form';
import StaffAccountVenueDropdown from './StaffAccountsVenueDropdown';

import './StaffAccountModal.scss';

const messages = {
  [USER_ROLE.OWNER]: defineMessages({
    addTitle: {
      id: 'staff-account.staff-modal.add-owner',
      defaultMessage: 'Add owner',
    },
    editTitle: {
      id: 'staff-account.staff-modal.edit-owner',
      defaultMessage: 'Edit owner',
    },
  }),
  [USER_ROLE.MANAGER]: defineMessages({
    addTitle: {
      id: 'staff-account.staff-modal.add-manager',
      defaultMessage: 'Add manager',
    },
    editTitle: {
      id: 'staff-account.staff-modal.edit-manager',
      defaultMessage: 'Edit manager',
    },
  }),
  [USER_ROLE.FRONT_DESK]: defineMessages({
    addTitle: {
      id: 'staff-account.staff-modal.add-front-desk',
      defaultMessage: 'Add front desk',
    },
    editTitle: {
      id: 'staff-account.staff-modal.edit-front-desk',
      defaultMessage: 'Edit front desk',
    },
  }),
  ...defineMessages({
    firstName: {
      id: 'staff-account.staff-modal.first-name',
      defaultMessage: 'First name',
    },
    lastName: {
      id: 'staff-account.staff-modal.last-name',
      defaultMessage: 'Last name',
    },
    emailAddress: {
      id: 'staff-account.staff-modal.email-address',
      defaultMessage: 'Email address',
    },
    venues: {
      id: 'staff-account.staff-modal.venues',
      defaultMessage: 'Venues',
    },
    cancel: {
      id: 'staff-account.staff-modal.cancel',
      defaultMessage: 'Cancel',
    },
    save: {
      id: 'staff-account.staff-modal.save',
      defaultMessage: 'Save',
    },
    deleteUser: {
      id: 'staff-account.staff-modal.delete-user',
      defaultMessage: 'Delete user',
    },
  }),
};

// FYI: Confirm modal doesn't handle the third button - it is necessary to merge custom button with childrens from there.
const injectDeleteButton = (buttons, onDelete, isCurrentUser) => {
  if (onDelete && !isCurrentUser) {
    return React.Children.map(buttons, wrapper => {
      const children = React.Children.toArray(wrapper.props.children);
      return React.cloneElement(wrapper, {
        children: [
          <Message
            as={Button}
            className="staff-account-modal__delete-button"
            styled="danger"
            key="delete"
            onClick={onDelete}
            message={messages.deleteUser}
          />,
          ...children,
        ],
      });
    });
  }

  return buttons;
};

const StaffAccountModal = ({
  accountType,
  venues,
  handleSubmit,
  onDelete,
  isCurrentUser,
  isEdit,
  disabledVenueIds,
  account = {},
}) => (
  <Confirm
    cancelNode={<Message message={messages.cancel} />}
    acceptNode={<Message message={messages.save} />}
    title={<Message message={messages[accountType][isEdit ? 'editTitle' : 'addTitle']} />}
    onConfirm={handleSubmit}
    size="extended">
    {({ title, buttons }) => (
      <div className="staff-account-modal">
        {title}
        <div className="grid ph-h pb-h">
          <Field
            className="grid__item md-1/2 lg-1/2"
            name="firstName"
            type="text"
            component={Input}
            label={messages.firstName}
            required
          />
          <Field
            className="grid__item md-1/2 lg-1/2"
            name="lastName"
            type="text"
            component={Input}
            label={messages.lastName}
            required
          />
          <When predicate={isEdit && !isCurrentUser}>
            <InputLabel className="grid__item" message={messages.emailAddress} />
            <div className="grid__item user-email mb-s">{account.email}</div>
          </When>

          <When predicate={!isEdit || isCurrentUser}>
            <Field
              className="grid__item"
              name="email"
              type="text"
              component={Input}
              label={messages.emailAddress}
              required
            />
          </When>
          <Field
            className="grid__item"
            name="venueIds"
            venues={venues}
            component={StaffAccountVenueDropdown}
            label={messages.venues}
            disabledVenueIds={disabledVenueIds}
          />
        </div>
        {injectDeleteButton(buttons, onDelete, isCurrentUser)}
      </div>
    )}
  </Confirm>
);

StaffAccountModal.defaultProps = {
  accountType: USER_ROLE.OWNER,
};

StaffAccountModal.propTypes = {
  handleSubmit: PropTypes.func,
  accountType: PropTypes.oneOf([USER_ROLE.OWNER, USER_ROLE.MANAGER, USER_ROLE.FRONT_DESK]),
  venues: manualUserVenuesPropTypes,
  onDelete: PropTypes.func,
  isCurrentUser: PropTypes.bool,
  isEdit: PropTypes.bool,
  disabledVenueIds: PropTypes.arrayOf(PropTypes.number),
  account: PropTypes.shape({
    email: PropTypes.string,
  }),
};

export default StaffAccountModal;
